import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import { Form, Formik } from "formik";
import * as Yup from "yup";

import useNotification from '../../../../core/hooks/use_notification';
import { SubmitButton } from '../../../common/button';
import { getProducerAction, getProducerState } from "../../../../features/product/get_producer";
import { createProducerNotificationData, getProducerNotificationData, updateProducerNotificationData } from "../../../../core/utils/notification_utils";
import { createProducerAction, createProducerState } from "../../../../features/product/create_producer";
import { updateProducerAction, updateProducerState } from "../../../../features/product/update_producer";
import { ApiRequestStatusEnum } from "../../../../core/enums/common/api";
import { GET_ROUTE_PRODUCER_FORMS, ROUTE_PRODUCERS } from "../../../../core/constants";
import TextFieldFormik from "../../../common/text_field";
import PageHeader from '../../../common/page_header';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from '../../../common/bottom_action_bar';

const ProducerPage = () => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { producerId } = useParams();

    const getProducer = useSelector(getProducerState);
    const createProducer = useSelector(createProducerState);
    const updateProducer = useSelector(updateProducerState);

    const initialValues = {
        name: (producerId && getProducer?.data?.name) || '',
    }

    const schema = Yup.object().shape({
        name: Yup.string().required()
    });

    useEffect(() => {
        producerId && dispatch(getProducerAction(producerId))
            .then(response => showErrorMessage((getProducerNotificationData(response))));
    }, [producerId, dispatch, showErrorMessage]);

    const onSubmit = (values) => {
        const form = { ...values };

        if (!producerId) {
            dispatch(createProducerAction({ form }))
                .then(response => {
                    showNotification(createProducerNotificationData(response));
                    response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(GET_ROUTE_PRODUCER_FORMS(response?.payload.id));
                });
        } else {
            dispatch(updateProducerAction({ form, id: producerId }))
                .then(response => {
                    showNotification(updateProducerNotificationData(response));
                });
        }
    }

    const renderView = () => {
        if (getProducer?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        return (
            <Formik
                initialValues={ initialValues }
                validationSchema={ schema }
                validateOnChange={ false }
                validateOnBlur={ false }
                onSubmit={ onSubmit }>
                { () => (
                    <Box component={ Form }
                         sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                        <Box sx={ { flexGrow: 1, mb: 4 } }>
                            <SectionPaper title="Dane podstawowe">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } sm={ 8 } md={ 6 }>
                                        <TextFieldFormik name="name" label="Nazwa producenta" required/>
                                    </Grid>
                                </Grid>
                            </SectionPaper>
                        </Box>

                        <BottomActionBar>
                            <SubmitButton isLoading={ !producerId ? createProducer?.loading : updateProducer?.loading }>
                                Zapisz
                            </SubmitButton>
                        </BottomActionBar>

                    </Box>
                ) }
            </Formik>
        );
    }

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>

            <PageHeader
                title={ producerId ? "Edycja producenta" : "Nowy producent" }
                showBack={ true }
                onBack={ () => navigate(ROUTE_PRODUCERS) }
                titleAdornment={
                    producerId && getProducer?.data && (
                        <Typography variant="h6" color="textSecondary">
                            ID: { producerId }
                        </Typography>
                    )
                }
            />

            { renderView() }

        </Box>
    );
}

export default ProducerPage;